/**
 * Centralized card UI state persistence.
 *
 * - Session prefs: stored in sessionStorage with a page-load-scoped token.
 *   Survive SPA navigation but reset on full page refresh.
 * - Durable prefs: stored in localStorage. Survive navigation and refresh.
 *
 * Keys are always `${resourceID}:${suffix}` so clearing by resource ID is trivial.
 */

import { writable } from 'svelte/store';

// Generated once per JS module load. SPA navigation reuses the same token;
// a full page refresh generates a new one, so old sessionStorage entries are ignored.
const PAGE_SESSION_TOKEN = Math.random().toString(36).slice(2);

/** Key identifying a collapsible card section, e.g. `machine-settings:section:Software Updates`. */
export const sectionStorageKey = (resourceID: string, title: string): string =>
  `${resourceID}:section:${title}`;

/**
 * Pending requests to expand card sections, each held until its section reads it. Lets a
 * component elsewhere on the page open a section that has not mounted yet, which a direct
 * component reference cannot do. Pass a nested section's ancestors too: a collapsed parent
 * does not render its children, so the child only mounts once the parent has expanded.
 */
export const sectionExpandRequest = writable<string[]>([]);

export const requestSectionExpand = (...keys: string[]): void =>
  sectionExpandRequest.set(keys);

export const claimSectionExpand = (key: string): void =>
  sectionExpandRequest.update((keys) => keys.filter((k) => k !== key));

export const clearSectionExpandRequest = (): void =>
  sectionExpandRequest.set([]);

const SESSION_PREFIX = 'card-session:';
const DURABLE_PREFIX = 'card-pref:';

export const getSessionPref = (key: string): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    return (
      sessionStorage.getItem(`${SESSION_PREFIX}${PAGE_SESSION_TOKEN}:${key}`) ??
      undefined
    );
  } catch {
    return undefined;
  }
};

export const setSessionPref = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      `${SESSION_PREFIX}${PAGE_SESSION_TOKEN}:${key}`,
      value
    );
  } catch {
    // sessionStorage may be unavailable (private browsing, quota exceeded)
  }
};

/** Clear all session prefs. Intended for use in tests to prevent state leaking between test cases. */
export const clearAllSessionPrefs = (): void => {
  if (typeof window === 'undefined') return;
  const toRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(SESSION_PREFIX)) toRemove.push(key);
  }
  for (const key of toRemove) sessionStorage.removeItem(key);
};

export const getDurablePref = (key: string): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    return localStorage.getItem(DURABLE_PREFIX + key) ?? undefined;
  } catch {
    return undefined;
  }
};

export const setDurablePref = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DURABLE_PREFIX + key, value);
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
};

export const removeDurablePref = (key: string): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(DURABLE_PREFIX + key);
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
};

/** Remove all session and durable prefs scoped to a resource ID. */
export const clearResourcePrefs = (resourceID: string): void => {
  if (typeof window === 'undefined') return;

  const sessionKeyPrefix = `${SESSION_PREFIX}${PAGE_SESSION_TOKEN}:${resourceID}:`;
  const sessionToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(sessionKeyPrefix)) sessionToRemove.push(key);
  }
  for (const key of sessionToRemove) sessionStorage.removeItem(key);

  const durablePrefix = `${DURABLE_PREFIX}${resourceID}:`;
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(durablePrefix)) toRemove.push(key);
  }
  for (const key of toRemove) localStorage.removeItem(key);
};
